// Ported from deexoptions-mobile-app/hooks/queries/useSurvey.ts.
//
// Adaptation note: mobile imports a module-level `baseAxios`, while this project exports
// `useAuthAxios()` as a hook (it needs `logout` from auth context for the 401 path), so
// each hook below calls it internally rather than importing a shared instance.

import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import useAuthAxios from "../../utils/baseAxios";
import { API } from "../../constants/api";

/* -------------------------------------------------------------------------- */
/* Types                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Where a survey CTA can appear. The admin picks these per survey; the client asks per
 * placement and simply gets `survey: null` where the admin didn't opt in, so the admin's
 * choice is what decides whether a given surface shows anything.
 */
export type SurveyPlacement =
  | "DASHBOARD_CTA"
  | "SETTINGS"
  | "TRANSACTION_SUCCESS";

export type SurveyQuestionType =
  | "SINGLE_CHOICE"
  | "MULTI_CHOICE"
  | "SHORT_TEXT"
  | "LONG_TEXT"
  | "NPS"
  | "RATING";

export type SurveyOption = { id: string; label: string };

export type SurveyQuestion = {
  key: string;
  prompt: string;
  helpText?: string | null;
  type: SurveyQuestionType | string;
  order: number;
  required: boolean;
  options?: SurveyOption[];
  allowOther?: boolean;
  otherPlaceholder?: string | null;
  /** MULTI_CHOICE only; `null` means no limit. */
  maxSelections?: number | null;
  placeholder?: string | null;
  maxLength?: number | null;
  /** Click-to-insert chips for text questions; answers record the `id`. */
  suggestions?: SurveyOption[];
  scale?: {
    min: number;
    max: number;
    minLabel?: string | null;
    maxLabel?: string | null;
  } | null;
};

/** Note: `title` is admin-facing and deliberately not returned to users. */
export type ActiveSurvey = {
  _id: string;
  description?: string | null;
  isAnonymous: boolean;
  rewardPoints?: number | null;
  endsAt?: string | null;
  questions: SurveyQuestion[];
};

/** The sentinel the API uses for an "Other" selection. */
export const OTHER_OPTION_ID = "__other__";

export type SurveyAnswerPayload = {
  questionKey: string;
  value: string | string[] | number;
  otherText?: string;
  /** Suggestion ids the user clicked, for text answers. */
  usedSuggestions?: string[];
};

type ActiveSurveyResponse = {
  status: string;
  data: { survey: ActiveSurvey | null };
};

type SurveyAttemptResponse = {
  status: string;
  data: {
    responseId: string;
    status: "PARTIAL" | "COMPLETED" | string;
    answers: unknown[];
    rewardPoints: number;
    /** Only returned by the start call; required on every save. */
    resumeToken?: string;
  };
};

/* -------------------------------------------------------------------------- */
/* Queries                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Root key for every placement's active-survey query.
 *
 * Completing or dismissing a survey changes the answer for *all* placements at once — the
 * backend stops offering it everywhere — so both actions invalidate this root rather than
 * one placement, otherwise the CTA lingers on the surfaces the user wasn't looking at.
 */
export const surveyQueryRoot = ["active-survey"] as const;

export const surveyQueryKey = (placement: SurveyPlacement) => [
  ...surveyQueryRoot,
  placement,
];

/**
 * The one survey this user should see at `placement`, or null. The backend already applies
 * status, dates, audience, prior completion and dismissals, so a non-null result always
 * means "show the CTA" — the client adds no eligibility logic of its own.
 *
 * Deliberately not persisted to localStorage: a stale cached survey would reproduce the
 * "CTA won't go away after completing" symptom for real, and make client bugs
 * indistinguishable from backend ones.
 */
export const useActiveSurvey = (placement: SurveyPlacement, enabled = true) => {
  const axiosInstance = useAuthAxios();

  return useQuery({
    queryKey: surveyQueryKey(placement),
    queryFn: async () => {
      const res = await axiosInstance.get<ActiveSurveyResponse>(
        API.activeSurvey(placement)
      );
      return res.data?.data?.survey ?? null;
    },
    enabled,
    // A survey does not change mid-session; don't refetch on every mount.
    staleTime: 5 * 60_000,
    // A missing survey is the normal case, not an error worth retrying hard.
    retry: 1,
  });
};

/* -------------------------------------------------------------------------- */
/* Mutations                                                                   */
/* -------------------------------------------------------------------------- */

/** Counted once per user server-side, so firing it on every CTA render is safe. */
export const useRecordSurveyView = () => {
  const axiosInstance = useAuthAxios();

  return useMutation<AxiosResponse, AxiosError["response"], string>({
    mutationFn: (surveyId: string) =>
      axiosInstance.post(API.recordSurveyView(surveyId), {}),
  });
};

/** "Not now" — the survey is never offered to this user again. Idempotent. */
export const useDismissSurvey = () => {
  const axiosInstance = useAuthAxios();

  return useMutation<AxiosResponse, AxiosError["response"], string>({
    mutationFn: (surveyId: string) =>
      axiosInstance.post(API.dismissSurvey(surveyId), {}),
  });
};

/**
 * Starts an attempt. On a non-anonymous survey calling this again resumes the user's open
 * attempt and issues a fresh `resumeToken`, so there is nothing to persist between visits.
 */
export const useStartSurveyResponse = () => {
  const axiosInstance = useAuthAxios();

  return useMutation<
    AxiosResponse<SurveyAttemptResponse>,
    AxiosError["response"],
    {
      surveyId: string;
      placement: SurveyPlacement;
      answers?: SurveyAnswerPayload[];
    }
  >({
    mutationFn: ({ surveyId, placement, answers }) =>
      axiosInstance.post(API.startSurveyResponse(surveyId), {
        placement,
        ...(answers?.length ? { answers } : {}),
      }),
  });
};

/**
 * Saves answers as the user advances (they merge by `questionKey`), and completes the
 * attempt when `submit` is true. `resumeToken` is required — a wrong or missing one reads
 * as a 404, not as a validation error.
 */
export const useSaveSurveyResponse = () => {
  const axiosInstance = useAuthAxios();

  return useMutation<
    AxiosResponse<SurveyAttemptResponse>,
    AxiosError["response"],
    {
      surveyId: string;
      responseId: string;
      resumeToken: string;
      answers: SurveyAnswerPayload[];
      submit?: boolean;
    }
  >({
    mutationFn: ({ surveyId, responseId, resumeToken, answers, submit }) =>
      axiosInstance.patch(API.saveSurveyResponse(surveyId, responseId), {
        resumeToken,
        answers,
        ...(submit ? { submit: true } : {}),
      }),
  });
};
